import { ChangeEvent, useEffect, useState } from "react";
import { useDesignerStore } from "../store";
import { designerEngine } from "../engine/DesignerEngine";
import { meApi } from "@/lib/api";

export default function UploadPanel() {
  const setImageInfo = useDesignerStore((state) => state.setImageInfo);
  const [artwork, setArtwork] = useState<any[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadArtwork() {
    try {
      setArtwork((await meApi.artwork()).artwork);
    } catch {
      setArtwork([]);
    }
  }
  useEffect(() => {
    loadArtwork();
  }, []);

  // Place an image (data-URL) onto the shirt / gang sheet.
  function place(url: string) {
    const img = new Image();
    img.onload = () => setImageInfo(img.naturalWidth, img.naturalHeight);
    img.src = url;
    designerEngine.addLogo(url);
  }

  async function onUpload(event: ChangeEvent<HTMLInputElement>) {
    setError("");
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";

    const dataUrl: string = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = (e) => resolve(e.target?.result as string);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

    // Always add it to the canvas immediately.
    place(dataUrl);

    // Persist to the user's library so it's available next time (size-capped for now).
    if (file.size > 700_000) {
      setError("Added to your design, but too large (>700KB) to save to your library yet.");
      return;
    }
    setBusy(true);
    try {
      await meApi.uploadArtwork({ filename: file.name, content_type: file.type, data: dataUrl });
      await loadArtwork();
    } catch (e: any) {
      setError(e.message || "Could not save to library");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <label className="block w-full cursor-pointer rounded-xl bg-slate-700 hover:bg-slate-600 py-3 text-center font-semibold">
        {busy ? "Uploading…" : "🖼 Upload logo / artwork"}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={onUpload}
        />
      </label>

      {error && <p className="text-amber-400 text-xs">{error}</p>}

      <div>
        <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Your artwork</p>
        {artwork === null ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : artwork.length === 0 ? (
          <p className="text-slate-500 text-sm">Nothing uploaded yet. Upload a logo to reuse it anytime.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {artwork.map((a) => (
              <button
                key={a.id}
                onClick={() => a.data && place(a.data)}
                title={`Add “${a.filename}”`}
                className="aspect-square rounded-lg bg-slate-800 border border-slate-700 hover:border-cyan-400 overflow-hidden flex items-center justify-center p-1 transition"
              >
                {a.content_type?.startsWith("image/") && a.data ? (
                  <img src={a.data} className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-2xl">🖼️</span>
                )}
              </button>
            ))}
          </div>
        )}
        <p className="text-slate-500 text-[11px] mt-2">Click any artwork to add it to your design.</p>
      </div>
    </div>
  );
}
